/* =====================================================================
 * PagePreviewCard — 워크스페이스 페이지 미리보기 + 원본 이동 버튼
 * ===================================================================== */

import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Eye } from 'lucide-react';
import { Button } from '@auw/workspace-ui';
import { useChatStore } from '@auw/stores';
import type { WorkspaceItem, WorkspaceMode } from '@auw/workspace-utils';
import { WORKSPACE_MODES } from '@auw/workspace-utils';
import { useWorkspaceNavigate } from '@auw/workspace-features';
import { formatDate } from './StorageFileCard';
import {
  PagePreviewWrap,
  PagePreviewHeader,
  PagePreviewIconWrap,
  PagePreviewInfo,
  PagePreviewTitle,
  PagePreviewMeta,
  PagePreviewActions,
} from './storage.styles';

interface Props {
  item: WorkspaceItem;
}

export const PagePreviewCard: React.FC<Props> = ({ item }) => {
  const { t } = useTranslation('common');
  const conversations = useChatStore((s) => s.conversations);
  const { openWorkspaceItem, openWorkspaceMode } = useWorkspaceNavigate();

  const modeInfo = WORKSPACE_MODES[item.mode as WorkspaceMode];
  const ModeIcon = modeInfo?.icon;

  const handleNavigate = useCallback(() => {
    const convId = item.session_id ?? item.item_id;
    const conv = conversations[convId];
    if (conv) {
      openWorkspaceItem(convId);
    } else {
      openWorkspaceMode(item.mode as WorkspaceMode);
    }
  }, [conversations, item, openWorkspaceItem, openWorkspaceMode]);

  return (
    <PagePreviewWrap>
      <PagePreviewHeader>
        {ModeIcon && (
          <PagePreviewIconWrap $color={modeInfo.color}>
            <ModeIcon size={22} />
          </PagePreviewIconWrap>
        )}
        <PagePreviewInfo>
          <PagePreviewTitle>{item.title}</PagePreviewTitle>
          <PagePreviewMeta>
            {modeInfo ? t(modeInfo.labelKey) : item.mode} &middot; {formatDate(item.updated_at)}
          </PagePreviewMeta>
        </PagePreviewInfo>
      </PagePreviewHeader>

      <PagePreviewActions>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<ExternalLink size={14} />}
          onClick={handleNavigate}
        >
          {t('storage.goToOriginal')}
        </Button>
      </PagePreviewActions>
    </PagePreviewWrap>
  );
};
